/**
 * Renderer hook for the GUI phase strip (Crucible #416 Chunk C).
 *
 * Subscribes to `phase-progress:update` for the active gameId. Returns
 * the latest `PhaseProgressUpdate` (or `undefined` while the initial
 * subscribe is in flight). Internally:
 *
 *   1. Calls `phase-progress:subscribe` once per gameId. The IPC
 *      returns the current snapshot synchronously, so the strip can
 *      paint immediately.
 *   2. Listens on `phase-progress:update` for subsequent push events
 *      from main's `fs.watch`.
 *   3. Calls `phase-progress:unsubscribe` on cleanup so main can tear
 *      down its watcher.
 *
 * This hook NEVER reads `docs/build-progress.json`. The wire payload
 * contains only the fields produced by `derivePhase` + `parseErrorToProse`
 * — we never see raw bytes from the file.
 */

import { useEffect, useState } from 'react';
import type { PhaseProgressUpdate } from '@volley/crucible-ipc-types/ipc-channels';
import { usePhaseEventsStore } from '../stores/phase-events';

export function usePhaseProgress(
  gameId: string | undefined,
): PhaseProgressUpdate | undefined {
  const [update, setUpdate] = useState<PhaseProgressUpdate | undefined>(
    undefined,
  );
  const setCurrentPhaseSeq = usePhaseEventsStore(
    (s) => s.setCurrentPhaseSeq,
  );
  const setGameId = usePhaseEventsStore((s) => s.setGameId);

  useEffect(() => {
    if (!gameId) {
      setUpdate(undefined);
      return;
    }
    // Issue #533 review round 2 (HIGH): hygiene — schedule a clear of
    // the prior game's update when the gameId effect fires. This is
    // load-bearing for the SECOND render after a route change (so a
    // late-arriving subscribe doesn't paint stale data) but is NOT
    // sufficient on its own for the first render: useEffect runs
    // post-commit, so React's first render after the gameId prop
    // change still commits with the old `update` value in state.
    // Downstream consumers (phase strip, chat status row) must still
    // tolerate a one-render cross-game stale window after a route
    // change; this hook-side clear narrows it.
    setUpdate(undefined);

    let cancelled = false;
    // Mirror the active gameId into the in-session events buffer so
    // events from a previous game don't bleed into the new game's
    // phase-scoped detail panel.
    setGameId(gameId);

    const unsub = window.crucible.on('phase-progress:update', (payload) => {
      if (cancelled) return;
      // Filter cross-game pushes — the renderer can have multiple
      // games' watchers active in main if a power user is bouncing
      // between Workspace tabs (we don't, today, but be defensive).
      if (payload.gameId !== gameId) return;
      setUpdate(payload);
      if (payload.kind === 'derived') {
        const last = payload.derived.transitions.at(-1);
        if (last) setCurrentPhaseSeq(last.seq);
      }
    });

    void window.crucible
      .invoke('phase-progress:subscribe', { gameId })
      .then((snapshot) => {
        if (cancelled) return;
        if (snapshot.gameId !== gameId) return;
        setUpdate(snapshot);
        if (snapshot.kind === 'derived') {
          const last = snapshot.derived.transitions.at(-1);
          if (last) setCurrentPhaseSeq(last.seq);
        }
      })
      .catch(() => {
        // Subscribe failures degrade gracefully — the strip just
        // doesn't render. Main-side errors are already surfaced
        // through `editor:error` and the diagnostic blob.
      });

    return () => {
      cancelled = true;
      unsub();
      void window.crucible
        .invoke('phase-progress:unsubscribe', { gameId })
        .catch(() => {
          // best effort — main tears down on quit anyway
        });
    };
  }, [gameId, setCurrentPhaseSeq, setGameId]);

  return update;
}
